import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import { useTenant } from "@/lib/tenant";
import { MapContainer, TileLayer, CircleMarker, Tooltip as LTooltip, GeoJSON, Polyline, useMap, LayersControl } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowLeft, Loader2, MapPinOff, RefreshCw, Route as RouteIcon } from "lucide-react";

const STATUS_COLOR = { ACTIVE: "#34d399", IDLE: "#fbbf24", OFFLINE: "#f87171", UNKNOWN: "#94a3b8" };
// Bright distinct colours for each tractor's breadcrumb trail
const TRAIL_COLOURS = ["#fb923c", "#22d3ee", "#a78bfa", "#fbbf24", "#f472b6", "#4ade80", "#60a5fa"];

function FitBounds({ points, geo }) {
  const map = useMap();
  useEffect(() => {
    if (geo && geo.features && geo.features.length) {
      try {
        const layer = L.geoJSON(geo);
        const b = layer.getBounds();
        if (b.isValid()) {
          map.fitBounds(b, { padding: [40, 40] });
          return;
        }
      } catch { /* fall through */ }
    }
    if (points.length === 1) map.setView(points[0], 13);
    else if (points.length > 1) map.fitBounds(points, { padding: [40, 40] });
  }, [points, geo, map]);
  return null;
}

export default function FleetMap() {
  const { api } = useTenant();
  const [fleet, setFleet] = useState([]);
  const [fields, setFields] = useState([]);
  const [trails, setTrails] = useState({});
  const [trailHours, setTrailHours] = useState(24);
  const [showTrails, setShowTrails] = useState(true);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/fieldops/fleet"),
      api.get("/fieldops/fields").catch(() => ({ data: { data: [] } })),
      api.get(`/fieldops/breadcrumbs?hours=${trailHours}`).catch(() => ({ data: { data: {} } })),
    ])
      .then(([f, fd, tr]) => {
        const fp = f.data;
        setFleet(Array.isArray(fp) ? fp : (fp?.data || []));
        setFields(fd.data?.data || []);
        setTrails(tr.data?.data || {});
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, [trailHours]); // eslint-disable-line

  const refreshFields = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/fieldops/fields/refresh");
      setFields(r.data?.data || []);
    } catch { /* ignore */ }
    finally { setRefreshing(false); }
  };

  const machinesWithLoc = fleet.filter(m => m.location?.lat && m.location?.lon);
  const points = machinesWithLoc.map(m => [m.location.lat, m.location.lon]);

  // Build a single GeoJSON FeatureCollection from all field boundaries.
  // CNH returns coordinates as GeoJSON-compliant [lng, lat] arrays.
  const geo = useMemo(() => {
    const features = [];
    for (const f of fields) {
      for (const b of f.boundaries || []) {
        if (!b.coordinates) continue;
        features.push({
          type: "Feature",
          properties: {
            field_id: f.field_id,
            name: f.field_name,
            farm: f.farm,
            grower: f.grower,
            area_ha: f.area_ha,
            boundary_type: b.boundary_type,
          },
          geometry: {
            type: b.type || "Polygon",
            coordinates: b.coordinates,
          },
        });
      }
    }
    return features.length ? { type: "FeatureCollection", features } : null;
  }, [fields]);

  const center = points[0]
    || (geo && geo.features[0]?.geometry?.coordinates?.[0]?.[0]
        ? [geo.features[0].geometry.coordinates[0][0][1], geo.features[0].geometry.coordinates[0][0][0]]
        : [57.243, -2.495]);

  const fieldStyle = (feature) => {
    const isInner = feature.properties.boundary_type === "INNER";
    return {
      color: "#ffffff",
      weight: 2,
      opacity: 1,
      fillColor: isInner ? "#000000" : "#fbbf24",
      fillOpacity: isInner ? 0.35 : 0.15,
      dashArray: isInner ? "4 3" : null,
    };
  };

  const onFieldClick = (feature, layer) => {
    const p = feature.properties;
    const html = `
      <div style="font:13px system-ui;min-width:160px">
        <div style="font-weight:700;color:#1a1a5c">${p.name || "Field"}</div>
        ${p.farm ? `<div style="color:#64748b;font-size:11px">${p.grower ? p.grower + " · " : ""}${p.farm}</div>` : ""}
        ${p.area_ha ? `<div style="margin-top:6px;color:#c41e3a;font-weight:600">${p.area_ha} ha</div>` : ""}
      </div>`;
    layer.bindTooltip(html, { sticky: true, direction: "top", className: "fleet-field-tip" });
  };

  return (
    <div className="h-screen flex flex-col">
      <div className="border-b border-white/10 bg-[#0a0e1c] px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/admin/fleet" data-testid="back-to-fleet" className="text-slate-400 hover:text-amber-300"><ArrowLeft size={16} /></Link>
          <h1 className="font-display font-bold text-lg">Fleet map</h1>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
          <span><strong className="text-slate-200">{machinesWithLoc.length}</strong>/{fleet.length} pinned</span>
          <span><strong className="text-amber-300">{fields.length}</strong> fields</span>
          <span><strong className="text-cyan-300">{Object.keys(trails).length}</strong> trails</span>
          <label className="inline-flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={showTrails} onChange={e => setShowTrails(e.target.checked)} className="accent-amber-400" />
            <RouteIcon size={11} /> Trails
          </label>
          <select value={trailHours} onChange={e => setTrailHours(Number(e.target.value))}
                  className="bg-white/5 border border-white/10 rounded px-2 py-0.5 text-xs">
            <option value={4}>4 h</option>
            <option value={12}>12 h</option>
            <option value={24}>24 h</option>
            <option value={72}>3 days</option>
            <option value={168}>7 days</option>
          </select>
          <button onClick={refreshFields} disabled={refreshing} data-testid="refresh-fields"
                  className="inline-flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 px-2 py-1 rounded text-xs">
            <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>
      <div className="flex-1 relative bg-slate-900">
        {loading && (
          <div className="absolute inset-0 grid place-items-center text-slate-500 text-sm z-[1000]">
            <Loader2 className="animate-spin mr-2" size={16} /> Loading map…
          </div>
        )}
        {!loading && !machinesWithLoc.length && !geo && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-amber-500/15 border border-amber-500/40 text-amber-100 text-xs px-4 py-2 rounded-md flex items-center gap-2 backdrop-blur">
            <MapPinOff size={14} /> No machine locations or fields yet — pins appear when a tractor moves; fields appear from your FieldOps farm setup.
          </div>
        )}

        <MapContainer center={center} zoom={13} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
          <LayersControl position="topright" collapsed={false}>
            <LayersControl.BaseLayer checked name="Satellite">
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution='Tiles © Esri'
                maxZoom={19}
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="OpenStreetMap">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='© OpenStreetMap'
              />
            </LayersControl.BaseLayer>
          </LayersControl>

          {/* Overlay labels & roads on top of satellite (separate from base) */}
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
            maxZoom={19} opacity={0.85} zIndex={250}
          />

          <FitBounds points={points} geo={geo} />

          {/* Field polygons */}
          {geo && (
            <GeoJSON key={geo.features.length} data={geo} style={fieldStyle} onEachFeature={onFieldClick} />
          )}

          {/* Breadcrumb trails per machine */}
          {showTrails && Object.entries(trails).map(([vin, points], i) => {
            const positions = points.map(([lat, lon]) => [lat, lon]);
            if (positions.length < 2) return null;
            const colour = TRAIL_COLOURS[i % TRAIL_COLOURS.length];
            const machine = fleet.find(m => m.vin === vin);
            return (
              <Polyline key={vin} positions={positions}
                        pathOptions={{ color: colour, weight: 3, opacity: 0.85, dashArray: "1 4" }}>
                <LTooltip sticky>
                  <strong>{machine?.nickname || vin}</strong><br />
                  {positions.length} points · last {trailHours}h
                </LTooltip>
              </Polyline>
            );
          })}

          {/* Machine pins */}
          {machinesWithLoc.map(m => (
            <CircleMarker key={m.vin} center={[m.location.lat, m.location.lon]} radius={10}
                          pathOptions={{
                            color: "#ffffff",
                            fillColor: STATUS_COLOR[m.status] || "#94a3b8",
                            fillOpacity: 0.95, weight: 2,
                          }}>
              <LTooltip permanent direction="top" offset={[0, -8]} className="fleet-machine-tip">
                <div className="text-xs">
                  <strong>{m.nickname || m.vin}</strong><br />
                  {m.model} · {m.status}{m.location_source === "last_known" ? " · last seen" : ""}
                </div>
              </LTooltip>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}
